import { useState } from 'react';
import { Copy, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SectionHeader } from '@/components/layout/SectionHeader';
import { EmptyState } from '@/components/states/EmptyState';
import {
  CustomDataset,
  CustomDatasetStatus,
  bnbaErrorOf,
  useBnbaCatalog,
  useBnbaDataset,
  useBnbaDatasets,
  useBuildBnbaDataset,
  useRevokeBnbaDataset,
} from '@/hooks/use-bnba-datasets';
import { BnbaDatasetBuilder } from './components/BnbaDatasetBuilder';
import { SensitivityBadge } from './components/FieldPicker';

const STATUS_LABEL: Record<CustomDatasetStatus, string> = {
  DRAFT: 'Draf',
  BUILDING: 'Sedang dibangun',
  READY: 'Aktif',
  FAILED: 'Gagal',
  REVOKED: 'Dicabut',
};

const STATUS_VARIANT: Record<
  CustomDatasetStatus,
  'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'
> = {
  DRAFT: 'secondary',
  BUILDING: 'warning',
  READY: 'success',
  FAILED: 'destructive',
  REVOKED: 'outline',
};

function progressOf(dataset: CustomDataset): string {
  const p = dataset.progress;
  if (dataset.status !== 'BUILDING' || !p) return '';
  const phase = p.phase === 'join' ? 'membaca set pasangan' : 'menulis baris';
  return `${phase} · ${p.batchesDone}/${p.batchesTotal} batch · ${p.rowsWritten.toLocaleString('id-ID')} baris`;
}

function DatasetDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: dataset } = useBnbaDataset(id);

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dataset?.name ?? 'Memuat…'}</DialogTitle>
          <DialogDescription>
            {dataset ? `${dataset.requester} · persetujuan ${dataset.approvalRef}` : ''}
          </DialogDescription>
        </DialogHeader>
        {dataset ? (
          <div className="space-y-4 text-sm">
            {dataset.accessUrl ? (
              <div className="space-y-1.5">
                <p className="font-semibold">Backend URL untuk SPLP</p>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 break-all rounded-md bg-surface-focus p-2 text-xs">
                    {dataset.accessUrl}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(dataset.accessUrl ?? '');
                      toast.success('URL disalin');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Daftarkan sebagai backend endpoint di SPLP. Di luar gateway URL ini menjawab 404,
                  termasuk dari browser.
                </p>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <p className="font-semibold">Kolom tersaji ({dataset.columns.length})</p>
              <div className="flex flex-wrap gap-2">
                {dataset.columns.map((column) => {
                  const flagged = dataset.sensitiveColumns.find((c) => c.column === column);
                  return (
                    <span key={column} className="inline-flex items-center gap-1.5">
                      <code className="text-xs">{column}</code>
                      {flagged ? <SensitivityBadge sensitivity={flagged.sensitivity} /> : null}
                    </span>
                  );
                })}
              </div>
            </div>

            {Object.keys({ ...dataset.filters, ...dataset.joinFilters }).length > 0 ? (
              <div className="space-y-1.5">
                <p className="font-semibold">Cakupan baris</p>
                <ul className="list-disc pl-4">
                  {Object.entries(dataset.filters).map(([k, v]) => (
                    <li key={k}>
                      <code className="text-xs">{k}</code>: {v.join(', ')}
                    </li>
                  ))}
                  {Object.entries(dataset.joinFilters).map(([k, v]) => (
                    <li key={`join-${k}`}>
                      <code className="text-xs">{k}</code> (set pasangan): {v.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {dataset.error ? <p className="text-destructive">{dataset.error}</p> : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function BnbaDatasetsPage() {
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<CustomDataset | null>(null);

  const catalog = useBnbaCatalog();
  const { data: datasets = [], isLoading, refetch } = useBnbaDatasets();
  const buildMutation = useBuildBnbaDataset();
  const revokeMutation = useRevokeBnbaDataset();

  const rebuild = async (dataset: CustomDataset) => {
    try {
      await buildMutation.mutateAsync(dataset.id);
      toast.success('Build dimulai.');
    } catch (error) {
      toast.error(bnbaErrorOf(error).message);
    }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast.success('Endpoint ditutup dan barisnya dihapus.');
    } catch (error) {
      toast.error(bnbaErrorOf(error).message);
    } finally {
      setRevokeTarget(null);
    }
  };

  if (creating) {
    return (
      <div className="space-y-4">
        <SectionHeader
          title="Dataset kustom BNBA baru"
          subtitle="Pilih field yang sudah disetujui, lalu rilis sebagai endpoint gated."
        />
        {catalog.data ? (
          <BnbaDatasetBuilder
            catalog={catalog.data}
            onDone={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {catalog.isError ? bnbaErrorOf(catalog.error).message : 'Memuat katalog field…'}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Dataset Kustom BNBA"
        subtitle="Subset DTSEN per permohonan, tersaji hanya lewat SPLP."
        metric={`${datasets.length} dataset`}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Buat dataset
            </Button>
          </>
        }
      />

      {!isLoading && datasets.length === 0 ? (
        <EmptyState
          title="Belum ada dataset kustom"
          description="Buat dataset dari permohonan yang sudah disetujui."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dataset</TableHead>
                  <TableHead>Pemohon</TableHead>
                  <TableHead>Set</TableHead>
                  <TableHead>Kolom</TableHead>
                  <TableHead>Baris</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {datasets.map((dataset) => (
                  <TableRow key={dataset.id}>
                    <TableCell>
                      <p className="font-medium">{dataset.name}</p>
                      <code className="text-xs text-muted-foreground">{dataset.tableName}</code>
                    </TableCell>
                    <TableCell>
                      <p>{dataset.requester}</p>
                      <p className="text-xs text-muted-foreground">{dataset.approvalRef}</p>
                    </TableCell>
                    <TableCell>{dataset.sourceSet}</TableCell>
                    <TableCell>
                      {dataset.columns.length}
                      {dataset.sensitiveColumns.some((c) => c.sensitivity === 'pii') ? (
                        <Badge variant="destructive" className="ml-2">
                          PII
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {dataset.dataset ? dataset.dataset.recordCount.toLocaleString('id-ID') : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[dataset.status]}>
                        {STATUS_LABEL[dataset.status]}
                      </Badge>
                      <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                        {dataset.status === 'FAILED' ? dataset.error : progressOf(dataset)}
                      </p>
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button variant="outline" size="sm" onClick={() => setDetailId(dataset.id)}>
                        Detail
                      </Button>
                      {dataset.status !== 'REVOKED' ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={dataset.status === 'BUILDING' || buildMutation.isPending}
                            onClick={() => rebuild(dataset)}
                          >
                            {dataset.generation > 0 ? 'Build ulang' : 'Build'}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={dataset.status === 'BUILDING'}
                            onClick={() => setRevokeTarget(dataset)}
                          >
                            Cabut
                          </Button>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {detailId ? <DatasetDetail id={detailId} onClose={() => setDetailId(null)} /> : null}

      <AlertDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => (open ? null : setRevokeTarget(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cabut {revokeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Endpoint ditutup dan seluruh barisnya dihapus. Tidak bisa dibatalkan; untuk
              membukanya lagi harus dibuat dataset baru dengan URL baru di SPLP.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={revoke}>Cabut</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
